import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home, TrendingUp, Download, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const cropData = [{
  crop: 'Maize',
  area: '2,100,000',
  production: '3,800,000',
  yield: '1.81',
  change: '+5.2%',
  trend: 'up'
}, {
  crop: 'Cassava',
  area: '1,400,000',
  production: '5,200,000',
  yield: '3.71',
  change: '+3.8%',
  trend: 'up'
}, {
  crop: 'Beans',
  area: '900,000',
  production: '1,100,000',
  yield: '1.22',
  change: '+2.1%',
  trend: 'up'
}, {
  crop: 'Rice (Paddy)',
  area: '180,000',
  production: '380,000',
  yield: '2.11',
  change: '+8.4%',
  trend: 'up'
}, {
  crop: 'Sorghum',
  area: '420,000',
  production: '460,000',
  yield: '1.10',
  change: '-1.3%',
  trend: 'down'
}, {
  crop: 'Sweet Potatoes',
  area: '650,000',
  production: '1,800,000',
  yield: '2.77',
  change: '+4.1%',
  trend: 'up'
}, {
  crop: 'Groundnuts',
  area: '380,000',
  production: '290,000',
  yield: '0.76',
  change: '+1.9%',
  trend: 'up'
}, {
  crop: 'Bananas (Cooking)',
  area: '1,500,000',
  production: '4,500,000',
  yield: '3.00',
  change: '+2.5%',
  trend: 'up'
}];
const livestockData = [{
  type: 'Cattle',
  population: '16,200,000',
  change: '+3.2%',
  trend: 'up'
}, {
  type: 'Goats',
  population: '16,800,000',
  change: '+4.5%',
  trend: 'up'
}, {
  type: 'Sheep',
  population: '4,200,000',
  change: '+2.8%',
  trend: 'up'
}, {
  type: 'Pigs',
  population: '4,500,000',
  change: '+5.1%',
  trend: 'up'
}, {
  type: 'Poultry',
  population: '47,600,000',
  change: '+6.3%',
  trend: 'up'
}, {
  type: 'Milk Production (Litres)',
  population: '2,800,000,000',
  change: '+4.7%',
  trend: 'up'
}];
const fishData = [{
  source: 'Lake Victoria',
  production: '248,000',
  change: '-3.2%',
  trend: 'down'
}, {
  source: 'Lake Albert',
  production: '98,000',
  change: '+1.5%',
  trend: 'up'
}, {
  source: 'Lake Kyoga',
  production: '72,000',
  change: '-1.8%',
  trend: 'down'
}, {
  source: 'Other Lakes & Rivers',
  production: '64,000',
  change: '+2.1%',
  trend: 'up'
}, {
  source: 'Aquaculture',
  production: '80,000',
  change: '+12.4%',
  trend: 'up'
}];
export function ProductionDataPage() {
  return <>
      <Header data-id="element-1287" />
      <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-1288">
        <div className="relative text-white py-32 md:py-48 overflow-hidden" data-id="element-1289">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1605000797499-95a51c5269ae?w=1600&h=600&fit=crop")'
        }} data-id="element-1290"></div>
          <div className="absolute inset-0 bg-uganda-green/80" data-id="element-1291"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-8" data-id="element-1292">
            <div className="flex items-center text-sm text-green-100 mb-6" data-id="element-1293">
              <Link to="/" className="hover:text-white flex items-center" data-id="element-1294">
                <Home className="w-4 h-4 mr-1" data-id="element-1295" /> Home
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1296" />
              <span data-id="element-1297">Data & Statistics</span>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1298" />
              <span className="text-white font-medium" data-id="element-1299">Production Data</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1300">
              Production Data
            </h1>
            <p className="text-lg text-green-50 max-w-3xl leading-relaxed" data-id="element-1301">
              Comprehensive crop, livestock, and fisheries production statistics
              for Uganda's agricultural sector.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-id="element-1302">
          {/* Crop Production */}
          <div className="flex items-center justify-between mb-6" data-id="element-1303">
            <h2 className="text-2xl font-bold text-uganda-black" data-id="element-1304">
              Crop Production (MT)
            </h2>
            <button className="flex items-center gap-2 text-sm font-semibold text-uganda-green hover:text-green-800 transition-colors" data-id="element-1305">
              <Download className="w-4 h-4" data-id="element-1306" /> Export CSV
            </button>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-12" data-id="element-1307">
            <div className="overflow-x-auto" data-id="element-1308">
              <table className="w-full" data-id="element-1309">
                <thead data-id="element-1310">
                  <tr className="bg-gray-50 border-b border-gray-200" data-id="element-1311">
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider" data-id="element-1312">
                      Crop
                    </th>
                    <th className="text-right px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider" data-id="element-1313">
                      Area (Ha)
                    </th>
                    <th className="text-right px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider" data-id="element-1314">
                      Production (MT)
                    </th>
                    <th className="text-right px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider hidden md:table-cell" data-id="element-1315">
                      Yield (MT/Ha)
                    </th>
                    <th className="text-right px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider" data-id="element-1316">
                      YoY Change
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1317">
                  {cropData.map((item, i) => <tr key={item.crop} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`} data-id="element-1318">
                      <td className="px-6 py-4 font-semibold text-uganda-black" data-id="element-1319">
                        {item.crop}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-700" data-id="element-1320">
                        {item.area}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-700 font-medium" data-id="element-1321">
                        {item.production}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-700 hidden md:table-cell" data-id="element-1322">
                        {item.yield}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-1323">
                        <span className={`inline-flex items-center gap-1 text-sm font-semibold ${item.trend === 'up' ? 'text-green-600' : 'text-red-500'}`} data-id="element-1324">
                          {item.trend === 'up' ? <ArrowUpRight className="w-4 h-4" data-id="element-1325" /> : <ArrowDownRight className="w-4 h-4" data-id="element-1326" />}
                          {item.change}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 bg-gray-50 text-xs text-gray-500 border-t border-gray-200" data-id="element-1327">
              Source: MAAIF Annual Agricultural Sector Performance Report FY
              2022/23. All figures in metric tonnes (MT) unless stated.
            </div>
          </div>

          {/* Livestock */}
          <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-1328">
            Livestock Population
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-id="element-1329">
            {livestockData.map((item, index) => <motion.div key={item.type} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.06
          }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-200" data-id="element-1330">
                <p className="text-sm text-gray-500 mb-1" data-id="element-1331">{item.type}</p>
                <div className="flex items-end justify-between" data-id="element-1332">
                  <p className="text-2xl font-bold text-uganda-black" data-id="element-1333">
                    {item.population}
                  </p>
                  <span className={`flex items-center gap-1 text-sm font-semibold ${item.trend === 'up' ? 'text-green-600' : 'text-red-500'}`} data-id="element-1334">
                    {item.trend === 'up' ? <ArrowUpRight className="w-4 h-4" data-id="element-1335" /> : <ArrowDownRight className="w-4 h-4" data-id="element-1336" />}
                    {item.change}
                  </span>
                </div>
              </motion.div>)}
          </div>

          {/* Fish Production */}
          <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-1337">
            Fish Production by Source (MT)
          </h2>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1338">
            <div className="overflow-x-auto" data-id="element-1339">
              <table className="w-full" data-id="element-1340">
                <thead data-id="element-1341">
                  <tr className="bg-gray-50 border-b border-gray-200" data-id="element-1342">
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider" data-id="element-1343">
                      Source
                    </th>
                    <th className="text-right px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider" data-id="element-1344">
                      Production (MT)
                    </th>
                    <th className="text-right px-6 py-4 text-xs font-bold text-gray-600 uppercase tracking-wider" data-id="element-1345">
                      YoY Change
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1346">
                  {fishData.map((item, i) => <tr key={item.source} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`} data-id="element-1347">
                      <td className="px-6 py-4 font-semibold text-uganda-black" data-id="element-1348">
                        {item.source}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-700 font-medium" data-id="element-1349">
                        {item.production}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-1350">
                        <span className={`inline-flex items-center gap-1 text-sm font-semibold ${item.trend === 'up' ? 'text-green-600' : 'text-red-500'}`} data-id="element-1351">
                          {item.trend === 'up' ? <ArrowUpRight className="w-4 h-4" data-id="element-1352" /> : <ArrowDownRight className="w-4 h-4" data-id="element-1353" />}
                          {item.change}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 bg-gray-50 text-xs text-gray-500 border-t border-gray-200" data-id="element-1354">
              Source: Directorate of Fisheries Resources, MAAIF 2023.
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-1355" />
    </>;
}